<template>
  <div
    class="irp-opportunity-list"
    :class="{ 'irp-opportunity-list--own': isOwnData }"
    data-testid="irp-opportunity-list"
  >
    <BittsEmptyState
      v-if="!rows.length"
      class="irp-opportunity-list__full-width"
      :font-awesome-icon="EMPTY_STATE.icon"
      :title="emptyMessage"
      :description="EMPTY_STATE.description"
      data-testid="irp-opportunity-list-empty"
    />
    <template v-else>
      <template v-for="(row, index) in rows" :key="`opportunity_${index}`">
        <div
          v-for="cell in row.cells"
          :key="cell.key"
          class="irp-opportunity-list__cell"
          :data-testid="`irp-opportunity-${cell.key}`"
        >
          <span class="irp-opportunity-list__label">{{ cell.label }}</span>
          <div class="irp-opportunity-list__value">
            <FontAwesomeIcon
              v-if="cell.key === 'owner'"
              :icon="['fak', 'person']"
              class="irp-opportunity-list__icon"
            />
            <IRPFieldGridText
              :class="{ 'irp-opportunity-list__amount': cell.key === 'amount' }"
              :text="cell.value"
              :is-masked="true"
            />
          </div>
        </div>

        <div
          v-if="row.productNames.length"
          class="irp-opportunity-list__cell irp-opportunity-list__full-width"
          data-testid="irp-opportunity-products"
        >
          <span class="irp-opportunity-list__label">
            {{ pluralize(row.productNames.length, 'Product') }}
          </span>
          <IRPFieldGridText
            :text="row.productNames.join(', ')"
            :is-masked="true"
          />
        </div>

        <div
          v-if="row.remainingFields.length"
          class="irp-opportunity-list__full-width"
        >
          <IRPAllFieldsCollapse :fields="row.remainingFields" />
        </div>

        <div
          v-if="index < rows.length - 1"
          class="irp-opportunity-list__separator"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { BittsEmptyState } from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { pluralize } from 'humanize-plus';
import { computed } from 'vue';

import { MDM_PROPERTIES, MDM_TYPES } from '@/constants/mdm';
import type { RecordOpportunityType } from '@/types/individual_record';

import { fieldCell, remainingFields } from './irp';
import IRPAllFieldsCollapse from './IRPAllFieldsCollapse.vue';
import IRPFieldGridText from './IRPFieldGridText.vue';

const {
  opportunities,
  isOwnData = false,
  emptyMessage = 'No opportunities to show',
} = defineProps<{
  opportunities: RecordOpportunityType[];
  isOwnData?: boolean;
  emptyMessage?: string;
}>();

const EMPTY_STATE: { description: string; icon: BittsIcon } = {
  description: 'Opportunities on this account will appear here.',
  icon: ['fak', 'opportunity'],
};

const DEAL_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.DEAL];

const CELLS = {
  name: { mdmProperty: DEAL_PROPERTIES.NAME, fallbackLabel: 'Name' },
  owner: { mdmProperty: DEAL_PROPERTIES.OWNER_ID, fallbackLabel: 'Owner' },
  amount: { mdmProperty: DEAL_PROPERTIES.AMOUNT, fallbackLabel: 'Amount' },
  'open-date': {
    mdmProperty: DEAL_PROPERTIES.OPEN_DATE,
    fallbackLabel: 'Open date',
  },
  'close-date': {
    mdmProperty: DEAL_PROPERTIES.CLOSE_DATE,
    fallbackLabel: 'Close date',
  },
  stage: { mdmProperty: DEAL_PROPERTIES.STAGE, fallbackLabel: 'Stage' },
} as const;

type CellKeyType = keyof typeof CELLS;

const OWN_CELL_KEYS: CellKeyType[] = [
  'name',
  'amount',
  'owner',
  'open-date',
  'close-date',
  'stage',
];

const PARTNER_CELL_KEYS: CellKeyType[] = [
  'name',
  'owner',
  'amount',
  'open-date',
  'close-date',
];

const cellKeys = computed(() =>
  isOwnData ? OWN_CELL_KEYS : PARTNER_CELL_KEYS,
);

const listedProperties = computed<string[]>(() =>
  cellKeys.value.map((key) => CELLS[key].mdmProperty),
);

const rows = computed(() =>
  opportunities.map((opportunity) => ({
    cells: cellKeys.value.map((key) => ({
      key,
      ...fieldCell(
        opportunity.fields,
        CELLS[key].mdmProperty,
        CELLS[key].fallbackLabel,
      ),
    })),
    remainingFields: remainingFields(
      opportunity.fields,
      listedProperties.value,
    ),
    productNames: opportunity.opportunity_products ?? [],
  })),
);
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

.irp-opportunity-list {
  @apply grid items-center gap-x-24 gap-y-16;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.irp-opportunity-list--own {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.irp-opportunity-list__full-width,
.irp-opportunity-list__separator {
  grid-column: 1 / -1;
}

.irp-opportunity-list__cell {
  @apply flex min-w-0 flex-col gap-4 text-base text-neutral-text;
}

.irp-opportunity-list__label {
  @apply truncate text-sm font-semibold text-neutral-text-weak;
}

.irp-opportunity-list__value {
  @apply flex min-w-0 items-center gap-8;
}

.irp-opportunity-list__icon {
  @apply shrink-0 text-neutral-accent;
}

.irp-opportunity-list__amount {
  @apply font-bold text-neutral-text-strong;
}

.irp-opportunity-list__separator {
  @apply border-t border-neutral-border;
}
</style>
